import { useState } from "react";
import {
  PAYMENT_KIND_LABELS,
  PAYMENT_METHOD_LABELS,
  formatCad,
  type AppointmentView,
  type Payment,
} from "@shared/modules/x-atelier";
import { Button } from "../../../core/components/ui/button";
import { Modal } from "../../../core/components/ui/modal";
import { formatDateTime } from "../../../core/lib/format";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { ApiError } from "../../../core/lib/api";
import {
  deletePayment,
  documentUrl,
  getAppointment,
  issueInvoice,
  issueQuote,
  sendDocument,
} from "../lib/api";
import { amountLines } from "../lib/taxes";
import { PaymentModal } from "./payment-modal";

interface PaymentsPanelProps {
  appointment: AppointmentView;
  /** Une autre action de la fiche est en cours : on ne cumule pas. */
  busy?: boolean;
  onChange: (view: AppointmentView) => void;
}

/**
 * Bloc « Montants » de la fiche de travail : sous-total, taxes, total, payé,
 * dû en évidence, liste des encaissements, et les actions Encaisser /
 * Facture / Devis. Les documents s'ouvrent dans un nouvel onglet (HTML
 * imprimable servi par l'API) après attribution du numéro s'il manque.
 */
export function PaymentsPanel({ appointment: a, busy = false, onChange }: PaymentsPanelProps) {
  const push = useToastStore((s) => s.push);
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");
  const [collecting, setCollecting] = useState(false);
  const [removing, setRemoving] = useState<Payment | null>(null);
  const [working, setWorking] = useState(false);

  const locked = busy || working;
  const settled = a.dueCents === 0 && a.totalWithTaxCents > 0;

  const openDocument = async (kind: "invoice" | "quote") => {
    if (locked) return;
    // L'onglet s'ouvre AVANT l'appel réseau : un `window.open` différé est
    // bloqué par les navigateurs comme fenêtre surgissante.
    const tab = window.open("", "_blank");
    setWorking(true);
    try {
      const hasNumber = kind === "invoice" ? a.invoiceNumber : a.quoteNumber;
      const view = hasNumber
        ? a
        : kind === "invoice"
          ? await issueInvoice(a.id)
          : await issueQuote(a.id);
      if (view !== a) onChange(view);
      const url = documentUrl(a.id, kind);
      if (tab) tab.location.href = url;
      else window.open(url, "_blank");
      if (!hasNumber) {
        push(
          "success",
          kind === "invoice" ? "Facture émise" : "Devis émis",
          kind === "invoice" ? (view.invoiceNumber ?? undefined) : (view.quoteNumber ?? undefined),
        );
      }
    } catch {
      tab?.close();
      push(
        "danger",
        kind === "invoice" ? "Facturation impossible" : "Émission du devis impossible",
      );
    } finally {
      setWorking(false);
    }
  };

  // Facture ou devis par courriel au client : numéro attribué au besoin, document
  // joint, message visible dans Messages. Sans adresse courriel, l'API refuse.
  const emailDocument = async (kind: "invoice" | "quote") => {
    if (locked) return;
    const label = kind === "invoice" ? "Facture" : "Devis";
    if (!window.confirm(`Envoyer ${kind === "invoice" ? "la facture" : "le devis"} par courriel au client ?`)) return;
    setWorking(true);
    try {
      const { message, appointment } = await sendDocument(a.id, kind);
      if (appointment !== a) onChange(appointment);
      if (message.status === "envoye") push("success", `${label} envoyée par courriel`, `À ${message.toAddress}.`);
      else if (message.status === "simule")
        push("warning", `${label} simulée`, "Aucune clé Mailjet sur ce serveur : rien n'est parti.");
      else push("danger", `${label} non envoyée`, message.error ?? undefined);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      push(
        "danger",
        `${label} non envoyée`,
        code === "client_email_missing"
          ? "Le client n'a pas d'adresse courriel dans sa fiche."
          : code === "message_opted_out"
            ? "Le client a refusé les communications."
            : undefined,
      );
    } finally {
      setWorking(false);
    }
  };

  const remove = async () => {
    if (!removing || locked) return;
    setWorking(true);
    try {
      await deletePayment(removing.id);
      onChange(await getAppointment(a.id));
      setRemoving(null);
      push("success", "Paiement supprimé");
    } catch {
      push("danger", "Suppression impossible");
    } finally {
      setWorking(false);
    }
  };

  return (
    <section className="card travail__block payments">
      <div className="travail__block-head">
        <h2 className="travail__block-title">Montants</h2>
        <span className="travail__block-actions">
          <Button
            variant="ghost"
            size="sm"
            disabled={locked}
            onClick={() => void openDocument("quote")}
          >
            {a.quoteNumber ? `Devis ${a.quoteNumber}` : "Devis"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={locked}
            onClick={() => void openDocument("invoice")}
          >
            {a.invoiceNumber ? `Facture ${a.invoiceNumber}` : "Facture"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={locked}
            title="Envoyer la facture (ou le devis s'il n'y a pas de facture) par courriel au client"
            onClick={() => void emailDocument(a.invoiceNumber || !a.quoteNumber ? "invoice" : "quote")}
          >
            Envoyer par courriel
          </Button>
          <Button variant="primary" size="sm" disabled={locked} onClick={() => setCollecting(true)}>
            Encaisser
          </Button>
        </span>
      </div>

      <dl className="payments__totals">
        {amountLines(a).map((line) => (
          <div
            key={line.key}
            className={[
              "payments__line",
              line.strong && "payments__line--strong",
              line.muted && "payments__line--muted",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <dt>{line.label}</dt>
            <dd>{formatCad(line.cents)}</dd>
          </div>
        ))}
        <div className={`payments__due${settled ? " payments__due--settled" : ""}`}>
          <dt>{settled ? "Payée" : "Dû"}</dt>
          <dd>{formatCad(a.dueCents)}</dd>
        </div>
      </dl>

      {a.payments.length === 0 ? (
        <p className="travail__empty">Aucun paiement enregistré.</p>
      ) : (
        <table className="table payments__table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Méthode</th>
              <th>Référence</th>
              <th className="travail__num">Montant</th>
              {isAdmin ? <th aria-label="Actions" /> : null}
            </tr>
          </thead>
          <tbody>
            {a.payments.map((p) => (
              <tr
                key={p.id}
                className={p.kind === "remboursement" ? "payments__refund" : undefined}
              >
                <td className="payments__date">{formatDateTime(p.paidAt, { dateStyle: "medium" })}</td>
                <td>{PAYMENT_KIND_LABELS[p.kind]}</td>
                <td>{PAYMENT_METHOD_LABELS[p.method]}</td>
                <td className="payments__ref">{p.reference ?? "—"}</td>
                <td className="travail__num">
                  {p.kind === "remboursement" ? "−" : ""}
                  {formatCad(p.amountCents)}
                </td>
                {isAdmin ? (
                  <td className="payments__actions">
                    <Button
                      variant="ghost-danger"
                      size="sm"
                      disabled={locked}
                      onClick={() => setRemoving(p)}
                    >
                      Supprimer
                    </Button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <PaymentModal
        open={collecting}
        appointment={a}
        onClose={() => setCollecting(false)}
        onSaved={(view) => {
          setCollecting(false);
          onChange(view);
        }}
      />

      <Modal
        open={removing !== null}
        title="Supprimer ce paiement ?"
        onClose={() => setRemoving(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)} disabled={working}>
              Annuler
            </Button>
            <Button variant="danger" disabled={working} onClick={() => void remove()}>
              Supprimer
            </Button>
          </>
        }
      >
        {removing ? (
          <p>
            {PAYMENT_KIND_LABELS[removing.kind]} de {formatCad(removing.amountCents)} (
            {PAYMENT_METHOD_LABELS[removing.method]}) du{" "}
            {formatDateTime(removing.paidAt, { dateStyle: "long" })}. Le payé et le dû seront
            recalculés.
          </p>
        ) : null}
      </Modal>
    </section>
  );
}
